import React from 'react';
import useDocumentContent from '../../../../../components/evidenceModal/useDocumentContent';

export default function useDeltaDocumentContent({activeStory, activeSegmentId}) {
    const activeSegment = React.useMemo(() => {
        if (!activeStory) return null;
        const safeSegments = Array.isArray(activeStory.segments) ? activeStory.segments : [];
        return safeSegments.find((segment) => String(segment?.segmentId) === String(activeSegmentId)) || null;
    }, [activeSegmentId, activeStory]);

    const activeDocumentId = activeSegment?.docId != null ? String(activeSegment.docId) : null;
    const documentContentState = useDocumentContent(activeDocumentId) || {isLoading: false, docData: null};
    const {isLoading: isDocumentLoading, docData} = documentContentState;

    const loadedDocumentId = docData?.docId != null ? String(docData.docId) : null;
    const isRequestedDocumentLoaded = !!activeDocumentId && loadedDocumentId === activeDocumentId;

    const activeSegmentIndexInDocument = React.useMemo(() => {
        if (!activeSegment?.segmentId) return null;

        const docSegments = Array.isArray(docData?.segmentsWithMetadata) ? docData.segmentsWithMetadata : [];
        const docSegmentIndex = docSegments.findIndex(
            (segment) => String(segment?.segmentId) === String(activeSegment.segmentId)
        );
        if (docSegmentIndex >= 0) return docSegmentIndex;

        if (!activeStory) return null;
        const storySegments = Array.isArray(activeStory.segments) ? activeStory.segments : [];
        const storySegmentIndex = storySegments.findIndex(
            (segment) => String(segment?.segmentId) === String(activeSegment.segmentId)
        );
        return storySegmentIndex >= 0 ? storySegmentIndex : null;
    }, [activeSegment?.segmentId, activeStory, docData?.segmentsWithMetadata]);

    const fallbackDocumentProps = React.useMemo(() => {
        if (!activeSegment) return null;

        const segmentContent = activeSegment?.content || '';
        return {
            docId: activeSegment?.docId ?? null,
            docTitle: activeSegment?.docTitle || `Document ${activeSegment?.docId ?? ''}`,
            docSource: null,
            docSourceType: null,
            docUrl: null,
            sourceScore: null,
            feedback: null,
            classificationLevel: null,
            segments: [segmentContent],
            segmentsWithMetadata: [{
                segmentId: activeSegment?.segmentId,
                content: segmentContent,
                segmentIndex: 0,
            }],
            tags: [],
            hasHTML: false,
            cssStyles: null,
        };
    }, [activeSegment]);

    const showDocumentLoading = !!activeDocumentId && (isDocumentLoading || !isRequestedDocumentLoaded);
    const documentContentProps = isRequestedDocumentLoaded ? docData : fallbackDocumentProps;

    return {
        activeSegment,
        activeDocumentId,
        activeSegmentIndexInDocument,
        showDocumentLoading,
        documentContentProps,
    };
}

